import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Calendar, AlertTriangle, CreditCard } from "lucide-react";

interface Subscription {
  tier: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  payment_method_type: string | null;
  payment_method_last4: string | null;
}

interface CurrentSubscriptionProps {
  subscription: Subscription | null;
}

export const CurrentSubscription = ({ subscription }: CurrentSubscriptionProps) => {
  if (!subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Current Subscription</CardTitle>
          <CardDescription>You don't have an active subscription</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPaymentMethod = () => {
    if (!subscription.payment_method_type || !subscription.payment_method_last4) {
      return 'No payment method on file';
    }
    return `${subscription.payment_method_type} ending in ${subscription.payment_method_last4}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Current Subscription</CardTitle>
        <CardDescription>Your current subscription details</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-muted-foreground">Current Plan</div>
            <div className="text-2xl font-bold capitalize">{subscription.tier}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-muted-foreground">Status</div>
            <div className="capitalize">{subscription.status}</div>
          </div>
          <div>
            <div className="text-sm font-medium text-muted-foreground">Payment Method</div>
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span>{formatPaymentMethod()}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div>
              <div className="text-sm font-medium text-muted-foreground">Current Period</div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">Cancel Subscription</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Cancel Subscription
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                <AlertDialogAction className="bg-destructive hover:bg-destructive/90">
                  Yes, Cancel Subscription
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};